/**
 * Component that renders the hero image on the SAT landing page.
 */
import "../../../../stylesheets/shared-package/roboto.less";

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {useMarkConversion} from "@repo/analytics-learning-time";
import {staticUrl} from "@repo/urls";

type Props = {
    // The URL that the CTA button should link to.
    signupUrl: string | null | undefined;
    title: React.ReactNode;
};

const mobilePartnershipUrl =
    "/images/rsat-landing/mobile_partnership-lockup.png";
const desktopPartnershipUrl =
    "/images/rsat-landing/desktop_partnership-lockup.png";

export default function HeroImage({
    signupUrl,
    title,
}: Props): React.ReactElement {
    const markConversion = useMarkConversion();

    return (
        <MediaLayout styleSheets={styleSheets}>
            {({mediaSize, styles}) => {
                const partnershipUrl =
                    mediaSize === "small"
                        ? mobilePartnershipUrl
                        : desktopPartnershipUrl;

                return (
                    <View style={styles.heroBackground}>
                        <View style={styles.heroBackgroundIllustration}>
                            <View style={styles.heroWrapper}>
                                <View style={styles.partnership}>
                                    <img
                                        alt={t`A partnership between College Board and Khan Academy`}
                                        src={staticUrl(partnershipUrl)}
                                        width="100%"
                                        height="auto"
                                    />
                                </View>
                                {/*
                                        This is a <h1> instead of a Wonder Blocks typography component
                                        because the font size does not conform to Wonder Blocks
                                        specifications, since we are aligning the style of this page
                                        with College Board's branding. */}
                                {/* @ts-expect-error - TS2345 - Argument of type 'StyleType' is not assignable to parameter of type 'CSSProperties | Falsy'. */}
                                <h1 className={css(styles.title)}>
                                    {/* @ts-expect-error - TS2345 - Argument of type 'StyleType' is not assignable to parameter of type 'CSSProperties | Falsy'. */}
                                    <mark className={css(styles.textHighlight)}>
                                        {title}
                                    </mark>
                                </h1>
                                <View style={styles.buttonWrapper}>
                                    <Button
                                        spinner={signupUrl == null}
                                        href={signupUrl ?? ""}
                                        safeWithNav={() =>
                                            markConversion({
                                                id: "sat_landing_page_cta_click",
                                                extra: {
                                                    Product: "test-prep",
                                                    Domain: "test-prep",
                                                    Subject: "sat",
                                                },
                                            })
                                        }
                                        size="large"
                                        style={styles.ctaButton}
                                    >
                                        {t`Let's go`}
                                    </Button>
                                </View>
                            </View>
                        </View>
                    </View>
                );
            }}
        </MediaLayout>
    );
}

const styleSheets = {
    all: StyleSheet.create({
        buttonWrapper: {
            display: "inline-block",
        },
        partnership: {
            display: "inline-block",
            position: "relative",
            boxSizing: "border-box",
            margin: "0 auto",
        },
        textHighlight: {
            backgroundColor: color.offWhite,

            // padding and boxDecorationArea together create a white box
            // with ragged edges around the text.
            padding: "2px 10px",
            boxDecorationBreak: "clone",
        },
    }),

    mdOrLarger: StyleSheet.create({
        ctaButton: {
            width: 240,
        },
        title: {
            // CollegeBoard made it a requirement to use their brand font, Roboto Bold,
            // in the hero module.
            fontFamily: "Roboto, Lato",
            fontSize: 54,
            lineHeight: 1.2,
            maxWidth: 800,
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: spacing.xSmall_8,
            marginBottom: 30,
        },
        heroWrapper: {
            display: "flex",
            flexDirection: "column",
            paddingTop: spacing.xxxLarge_64,
            paddingBottom: 56,
            textAlign: "center",
        },
        partnership: {
            width: 373,
        },
    }),

    small: StyleSheet.create({
        // Note(nisha): The following styles are used. The linter is confused
        // on the last style so we have to disable eslint.
        // eslint-disable-next-line react-native/no-unused-styles
        title: {
            // CollegeBoard made it a requirement to use their brand font, Roboto Bold,
            // in the hero module.
            fontFamily: "Roboto, Lato",
            fontSize: 30,
            lineHeight: 1.2,
            maxWidth: 266,
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: 12,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        heroWrapper: {
            display: "flex",
            flexDirection: "column",
            paddingTop: 33,
            paddingBottom: 45,
            textAlign: "center",
        },
        // eslint-disable-next-line react-native/no-unused-styles
        partnership: {
            width: 179,
        },
    }),
} as const;
